import { useWorkspacesFeatureEnabled } from '@editor/global/growthbookUtils';

import {
  usePlanSelectionStore,
  planSelectionStoreSelector,
} from '../../../stores/planSelectionStore';
import { isYearlySubscription } from '../utils';

import { SubscriptionHookParams, SubscriptionChangeState } from './types';

/**
 * Checks if any of the parameters of the subscription have changed according to the plan selection store
 * @param currentSubscription - The current subscription
 * @returns The subscription change state or null if no changes are detected
 */
const useSubscriptionChangeState = ({
  currentSubscription,
}: SubscriptionHookParams): SubscriptionChangeState | null => {
  const workspaceFeatureEnabled = useWorkspacesFeatureEnabled();
  const plan = usePlanSelectionStore(planSelectionStoreSelector.plan);
  const yearly = usePlanSelectionStore(planSelectionStoreSelector.yearly);
  const expertAddonPriceId = usePlanSelectionStore(
    planSelectionStoreSelector.expertAddonPriceId
  );
  const quantity = usePlanSelectionStore(planSelectionStoreSelector.quantity);

  const planChanged = currentSubscription?.plan?.name !== plan;
  const intervalChanged = yearly !== isYearlySubscription(currentSubscription);
  const expertAddonChanged =
    !!currentSubscription?.plan &&
    'addon' in currentSubscription.plan &&
    (currentSubscription.plan?.addon?.priceId ?? null) !== expertAddonPriceId;
  const quantityChanged =
    // We don't need to check the quantity if the workspace feature is enabled
    !workspaceFeatureEnabled &&
    !!currentSubscription?.plan &&
    'quantity' in currentSubscription.plan &&
    currentSubscription?.plan?.quantity !== quantity;

  const isChanged =
    planChanged || intervalChanged || expertAddonChanged || quantityChanged;

  return !isChanged
    ? null
    : {
        planChanged,
        intervalChanged,
        expertAddonChanged,
        quantityChanged,
      };
};

export default useSubscriptionChangeState;
